import React, { useState, useEffect } from 'react';
import { prosthesisApi } from '../../services/api';
import { Prestataire } from '../../types';
import PrestataireForm from './PrestataireForm';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Building,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface PrestataireManagementProps {
  isVisible: boolean;
}

const PrestataireManagement: React.FC<PrestataireManagementProps> = ({ isVisible }) => {
  const [prestataires, setPrestataires] = useState<Prestataire[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPrestataire, setEditingPrestataire] = useState<Prestataire | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchPrestataires();
  }, []);

  const fetchPrestataires = async () => {
    try {
      setLoading(true);
      const response = await prosthesisApi.getPrestataires();
      setPrestataires(response.data.results || []);
    } catch (error) {
      console.error('Erreur lors du chargement des prestataires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: Omit<Prestataire, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setFormLoading(true);
      if (editingPrestataire) {
        await prosthesisApi.updatePrestataire(editingPrestataire.id, data);
      } else {
        await prosthesisApi.createPrestataire(data);
      }
      await fetchPrestataires();
      setShowForm(false);
      setEditingPrestataire(null);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (prestataire: Prestataire) => {
    setEditingPrestataire(prestataire);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce prestataire ?')) {
      try {
        await prosthesisApi.deletePrestataire(id);
        await fetchPrestataires();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPrestataire(null);
  };

  const filteredPrestataires = prestataires.filter(prestataire =>
    prestataire.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prestataire.contact_principal.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prestataire.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (showForm) {
    return (
      <PrestataireForm
        prestataire={editingPrestataire}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={formLoading}
      />
    );
  }

  if (!isVisible) return null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Prestataires</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Ajouter un prestataire
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher par nom, contact ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      </div>

      {/* Liste des prestataires */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredPrestataires.length === 0 ? (
          <div className="text-center py-12">
            <Building className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun prestataire</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Aucun prestataire ne correspond à votre recherche.' : 'Commencez par ajouter un prestataire.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prestataire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Délai moyen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPrestataires.map((prestataire) => (
                  <tr key={prestataire.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building className="h-8 w-8 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {prestataire.nom}
                          </div>
                          <div className="text-sm text-gray-500">
                            {prestataire.contact_principal}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center gap-1 mb-1">
                          <Phone className="h-3 w-3 text-gray-400" />
                          {prestataire.telephone}
                        </div>
                        <div className="flex items-center gap-1 mb-1">
                          <Mail className="h-3 w-3 text-gray-400" />
                          {prestataire.email}
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {prestataire.adresse.substring(0, 30)}...
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {prestataire.delai_fabrication_moyen} jour{prestataire.delai_fabrication_moyen > 1 ? 's' : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        prestataire.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {prestataire.is_active ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Actif
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactif
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(prestataire)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(prestataire.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Building className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total prestataires</p>
              <p className="text-lg font-semibold text-gray-900">{prestataires.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Prestataires actifs</p>
              <p className="text-lg font-semibold text-gray-900">
                {prestataires.filter(p => p.is_active).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Délai moyen</p>
              <p className="text-lg font-semibold text-gray-900">
                {prestataires.length > 0 
                  ? Math.round(prestataires.reduce((acc, p) => acc + p.delai_fabrication_moyen, 0) / prestataires.length)
                  : 0
                } jours
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrestataireManagement;